import stylesheet from './PartnerListTable.scss?inline';

import CustomComponent from "../../base/customComponent/CustomComponent";
import {NXTSelectInput} from "../../parts/Input/Select";
import {NXTTextInput} from "../../parts/Input/TextInput";
import {breakpoints, getCurrentViewWidth} from "../../util/breakpoints";
import {isEditMode} from "../../util/magnolia";

class NXTPartnerListComponent extends CustomComponent {
    searchInput: NXTTextInput;
    tableContainer: HTMLElement;

    isFetchPending = false;
    isFetching = false;
    offset = 0;
    lastLimit: number | null = null;

    constructor() {
        super();
        this.customStyle = stylesheet;
        // @ts-ignore
        import('./PartnerListRow');
    }

    get api(): string {
        return this.getAttribute('api') ?? '';
    }

    get skeleton(): boolean {
        return this.getAttribute('skeleton') !== "false";
    }

    get countryFilterValue(): string | undefined {
        return this.querySelector<NXTSelectInput>('[slot="filter-country"]')?.value;
    }

    get typeFilterValue(): string | undefined {
        return this.querySelector<NXTSelectInput>('[slot="filter-type"]')?.value;
    }

    get limitForViewport(): number {
        const vw = getCurrentViewWidth();
        if (vw >= breakpoints.lg) {
            return 5;
        } else if (vw >= breakpoints.md) {
            return 3;
        } else {
            return 2;
        }
    }

    render() {
        const container = document.createElement('div');
        container.innerHTML = `
            <div class="filters">
                <nxt-text-input type="search" variant="search-expandable" size="lg" size-lg="md"></nxt-text-input>
                <slot name="filter-country"></slot>
                <slot name="filter-type"></slot>
            </div>
            <div class="body">
                <div class="table">
                    <div class="thead">
                        <slot name="thead"></slot>
                    </div>
                    <div class="tbody">
                        <div class="skeleton-frame"></div>
                        <slot name="tbody"></slot>
                    </div>
                    <div class="tfoot">
                        <slot name="tfoot"></slot>
                    </div>
                </div>
                <slot></slot>
            </div>
        `;
        // Store Element references
        this.searchInput = container.querySelector('nxt-text-input');
        this.tableContainer = container.querySelector('.table');
        // Attach events
        this.searchInput.addEventListener('blur', () => {
            this.resetOffset();
            this.fetchData();
        });
        this.searchInput.addEventListener('keyup', (ev) => {
            if (ev.key === 'Enter') {
                this.resetOffset();
                this.fetchData();
            }
        });
        this.querySelector('[slot="filter-country"]')?.addEventListener('change', () => {
            this.resetOffset();
            this.fetchData();
        });
        this.querySelector('[slot="filter-type"]')?.addEventListener('change', () => {
            this.resetOffset();
            this.fetchData();
        });
        this.tableContainer.addEventListener('prev', () => {
            this.changeOffset(-this.limitForViewport);
            this.fetchData();
        });
        this.tableContainer.addEventListener('next', () => {
            this.changeOffset(this.lastLimit);
            this.fetchData();
        });
        // Initialize: Fetch on page load
        if (!isEditMode()) {
            if (this.skeleton) {
                this.renderSkeleton();
            }
            this.fetchData();
        }
        return container;
    }

    renderSkeletonDivRow() {
        return `
            <nxt-partner-list-row location="tbody">
                <div class="td">
                    <div class="skeleton skeleton-logo" ></div>
                </div>
                <div class="td">
                    <div class="skeleton skeleton-line" ></div>
                    <div class="skeleton skeleton-line-75" ></div>
                </div>
                <div class="td">
                    <div class="skeleton skeleton-line" ></div>
                </div>
                <div class="td">
                    <div class="skeleton skeleton-button-xs" ></div>
                </div>
            </nxt-partner-list-row>
        `;
    }

    renderSkeleton() {
        let skeletonTableBody = '';
        for (let i = 0; i < 5; i++) {
            skeletonTableBody += this.renderSkeletonDivRow();
        }
        this.tableContainer.querySelector('.skeleton-frame').innerHTML = skeletonTableBody;
    }

    resetOffset() {
        this.offset = 0;
    }

    changeOffset(delta: number) {
        this.offset += delta;
        if (this.offset < 0) {
            this.offset = 0;
        }
    }

    buildApiUrl(url: string, query: { [k: string]: string }): string {
        const apiUrl = new URL(url, location.href);
        const params = apiUrl.searchParams;
        for (const key of Object.keys(query)) {
            if (query[key]) {
                params.set(key, query[key]);
            }
        }
        return apiUrl.toString();
    }

    fetchData() {
        if (!this.api || isEditMode()) {
            return;
        }
        if (this.isFetching) {
            this.isFetchPending = true;
            return;
        }
        this.isFetching = true;
        this.lastLimit = this.limitForViewport;
        fetch(this.buildApiUrl(this.api, {
            country: this.countryFilterValue,
            type: this.typeFilterValue,
            q: this.searchInput.value,
            offset: this.offset.toString(),
            limit: this.limitForViewport.toString(),
        })).then(async (response) => {
            if (response.ok) {
                const responseMarkup = document.createElement('div');
                responseMarkup.innerHTML = await response.text();
                this.tableContainer.querySelector<HTMLElement>('.skeleton-frame').style.display = 'none';
                this.updateSlotIfExists(responseMarkup, 'filter-country');
                this.updateSlotIfExists(responseMarkup, 'filter-type');
                this.updateSlotIfExists(responseMarkup, 'thead');
                this.updateSlotIfExists(responseMarkup, 'tbody');
                this.updateSlotIfExists(responseMarkup, 'tfoot');
            }
        }).finally(() => {
            this.isFetching = false;
            if (this.isFetchPending) {
                this.isFetchPending = false;
                this.fetchData();
            }
        });
    }

}

customElements.define('nxt-partner-list-component', NXTPartnerListComponent);
